// initRum — the RUM entry point every SPA calls once from its main.tsx.
//
// Wires Core Web Vitals + the client-error pipeline into a batch queue and flushes
// both to the backend (POST /api/rum, POST /api/client-errors) when the page is
// backgrounded or unloading — the only moments the browser lets us reliably send
// final telemetry. All work is best-effort and off the render path.
//
// Sampling is decided ONCE here: a sampled-out page installs nothing and stays
// dormant (`reportClientError` is a no-op). Transport defaults by app: seller (a
// cross-origin CDN) uses keepalive `fetch` to its absolute API origin; web/admin
// (same-origin) use `navigator.sendBeacon`.

import { sendBatch, type Transport } from "./beacon";
import {
  drainErrors,
  installGlobalErrorHandlers,
  reportPusherHealth,
  setErrorsEnabled,
} from "./errors";
import type { ClientErrorBatchRequest, RumBatchRequest, RumConfig, RumVital } from "./types";
import { registerVitals } from "./web-vitals";
import { setPusherHealthHandler } from "../pusher/pusher-connection";

const RUM_PATH = "/api/rum";
const ERRORS_PATH = "/api/client-errors";
const URL_MAX = 512; // RumBatchRequest.url / ClientErrorBatchRequest.url
const MAX_VITALS = 24; // RumBatchRequest.vitals

let initialized = false;
let vitalsQueue: RumVital[] = [];
let removeErrorHandlers: (() => void) | null = null;
let onVisibility: (() => void) | null = null;
let onPageHide: (() => void) | null = null;

function currentUrl(): string {
  // Path only — origin + pathname, deliberately dropping the query string and
  // fragment. `?debug=1` today (and any future token/PII-bearing param) must
  // never ride a RUM / client-error beacon into a Cloud Logging line.
  if (typeof location === "undefined") return "";
  const url = location.origin + location.pathname;
  return url.length > URL_MAX ? url.slice(0, URL_MAX) : url;
}

/**
 * Initialize Real User Monitoring for this page. Idempotent — a second call is a
 * no-op. See {@link RumConfig} for the transport/sampling defaults.
 */
export function initRum(config: RumConfig): void {
  if (initialized) return;

  const sampleRate = config.sampleRate ?? 1;
  // Decide inclusion once, up front. A sampled-out page installs nothing.
  if (!(Math.random() < sampleRate)) return;
  initialized = true;

  const { app } = config;
  const transport: Transport = config.transport ?? (app === "seller" ? "fetch" : "beacon");
  const endpoint = config.endpoint ?? "";

  setErrorsEnabled(true);

  const flushVitals = (): void => {
    if (vitalsQueue.length === 0) return;
    const batch: RumBatchRequest = {
      app,
      url: currentUrl(),
      vitals: vitalsQueue.slice(0, MAX_VITALS),
      ts: Date.now(),
    };
    vitalsQueue = [];
    sendBatch(RUM_PATH, batch, transport, endpoint);
  };
  const flushErrors = (): void => {
    const errors = drainErrors();
    if (errors.length === 0) return;
    const batch: ClientErrorBatchRequest = {
      app,
      url: currentUrl(),
      errors,
      ts: Date.now(),
    };
    sendBatch(ERRORS_PATH, batch, transport, endpoint);
  };
  const flushAll = (): void => {
    flushVitals();
    flushErrors();
  };

  registerVitals((vital) => {
    // web-vitals emits each metric at most once; the cap is belt-and-suspenders.
    if (vitalsQueue.length < MAX_VITALS) vitalsQueue.push(vital);
  });

  removeErrorHandlers = installGlobalErrorHandlers();
  // Invert the dependency: rum registers its error queue as the pusher manager's
  // health consumer, so pusher/ never imports rum.
  setPusherHealthHandler(reportPusherHealth);

  // Flush when the page is backgrounded (visibilitychange→hidden) or torn down
  // (pagehide). visibilitychange fires on tab switch / minimize on every platform;
  // pagehide covers bfcache eviction + real unloads that skip a hide. No `unload`
  // handler (it disables bfcache and is unreliable on mobile).
  if (typeof document !== "undefined") {
    onVisibility = (): void => {
      if (document.visibilityState === "hidden") flushAll();
    };
    document.addEventListener("visibilitychange", onVisibility);
  }
  if (typeof window !== "undefined") {
    onPageHide = flushAll;
    window.addEventListener("pagehide", onPageHide);
  }
}

/** Test-only: tear down all listeners + queues so each test starts clean. */
export function __resetRum(): void {
  if (removeErrorHandlers) {
    removeErrorHandlers();
    removeErrorHandlers = null;
  }
  if (onVisibility && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibility);
    onVisibility = null;
  }
  if (onPageHide && typeof window !== "undefined") {
    window.removeEventListener("pagehide", onPageHide);
    onPageHide = null;
  }
  setPusherHealthHandler(null);
  vitalsQueue = [];
  initialized = false;
}
